import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Trash, Sparkle, ArrowsLeftRight } from "@phosphor-icons/react";
import { useI18n } from "@/lib/i18n";
import { rankSimilarRecipes } from "@/lib/recipeSimilarity";

const LEVEL_LABEL = {
  identical: "Aynı reçete yapısı",
  very_similar: "Çok benzer",
  similar: "Benzer",
  related: "İlişkili",
  low: "Düşük benzerlik",
};

export default function Recipes() {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const [referenceId, setReferenceId] = useState("");
  const load = async () => {
    const rows = (await api.get("/recipes")).data || [];
    setList(rows);
    setReferenceId((prev) => prev && rows.some((r) => r.id === prev) ? prev : (rows[0]?.id || ""));
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm(t("final.recipe_delete"))) return;
    await api.delete(`/recipes/${id}`); toast.success(t("final.delete_success")); load();
  };

  const reference = useMemo(() => list.find((r) => r.id === referenceId) || null, [list, referenceId]);
  const matches = useMemo(() => rankSimilarRecipes(reference, list, 5), [reference, list]);

  return (
    <div>
      <PageHeader title={t("recipes.title")} subtitle={t("recipes.subtitle")} />
      <div className="p-4 sm:p-8 space-y-5">
        <div className="bg-white border border-slate-200 p-4 sm:p-5" data-testid="recipe-similarity-engine">
          <div className="flex flex-col lg:flex-row lg:items-end gap-4 justify-between">
            <div>
              <div className="flex items-center gap-2 text-slate-900 font-bold"><Sparkle size={18} weight="fill" /> Recipe Similarity Engine</div>
              <div className="text-xs text-slate-500 mt-1">Onaylı reçeteleri SKU yapısı ve miktar dağılımına göre karşılaştırır. Aynı palet tipindeki en yakın reçeteleri öne çıkarır.</div>
            </div>
            <label className="text-xs font-semibold text-slate-600 min-w-[280px]">
              Referans reçete
              <select value={referenceId} onChange={(e) => setReferenceId(e.target.value)} className="mt-1 w-full border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="similarity-reference-select">
                {list.map((r) => <option key={r.id} value={r.id}>{r.shipment_name || r.signature || r.id}</option>)}
              </select>
            </label>
          </div>

          {reference && <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-2">
            {matches.map((m) => (
              <div key={m.recipe.id} className="border border-slate-200 bg-slate-50 p-3" data-testid={`similar-recipe-${m.recipe.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="font-semibold text-sm truncate" title={m.recipe.shipment_name || m.recipe.signature}>{m.recipe.shipment_name || "Reçete"}</div>
                  <div className="font-mono text-lg font-extrabold text-[#2563EB]">%{m.percent.toFixed(1)}</div>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{LEVEL_LABEL[m.level]}</div>
                <div className="mt-3 text-[11px] text-slate-600 space-y-1">
                  <div className="flex justify-between"><span>SKU örtüşmesi</span><b>%{m.details.sku_overlap.toFixed(1)}</b></div>
                  <div className="flex justify-between"><span>Miktar örtüşmesi</span><b>%{m.details.qty_overlap.toFixed(1)}</b></div>
                  <div className="flex justify-between"><span>Toplam adet yakınlığı</span><b>%{m.details.total_closeness.toFixed(1)}</b></div>
                  <div className="flex justify-between"><span>Palet</span><b>{m.recipe.result?.pallets?.length || 0}</b></div>
                </div>
              </div>
            ))}
            {matches.length === 0 && <div className="xl:col-span-5 py-5 text-sm text-slate-400">Karşılaştırılabilecek aynı palet tipinde başka reçete yok.</div>}
          </div>}
        </div>

        <div className="bg-white border border-slate-200 overflow-x-auto rounded-sm">
          <table className="data-table w-full min-w-[760px]">
            <thead><tr>
              <th>{t("recipes.shipment")}</th><th>{t("recipes.country")}</th><th className="text-right">{t("recipes.products")}</th>
              <th className="text-right">{t("recipes.pallet")}</th><th className="text-right">{t("recipes.fill")}</th>
              <th>{t("recipes.signature")}</th><th></th>
            </tr></thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id} data-testid={`recipe-${r.id}`} className={r.id === referenceId ? "bg-blue-50/60" : ""}>
                  <td>
                    <button onClick={() => setReferenceId(r.id)} className="inline-flex items-center gap-1.5 text-left hover:text-[#2563EB]" title="Benzer reçeteleri bul">
                      {r.id === referenceId && <ArrowsLeftRight size={13} weight="bold" />}{r.shipment_name || "—"}
                    </button>
                    {r.inventory_adjusted && <div className="text-[10px] text-amber-700 mt-1" title={r.declared_signature || ""}>Eski adet kaydı farklı: yerleşimdeki gerçek kutular gösteriliyor.</div>}
                  </td>
                  <td>{r.country || "—"}</td>
                  <td className="text-right data-cell">{r.items?.length || 0}</td>
                  <td className="text-right data-cell">{r.result?.pallets?.length || 0}</td>
                  <td className="text-right data-cell">{((r.result?.fill_ratio || 0) * 100).toFixed(1)}%</td>
                  <td className="font-mono text-[11px] text-slate-500 max-w-md truncate">{r.signature}</td>
                  <td className="text-right"><button onClick={() => del(r.id)} className="text-slate-400 hover:text-red-600"><Trash size={16} /></button></td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-slate-400">{t("recipes.none")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

